import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/transitional/components/View';
import CurrentPlanButton from 'pricing-pages-lib/buttons/CurrentPlanButton';
import { CustomError } from 'pricing-pages-lib/errors/CustomErrors';
import { getButtonVariantProps, getTrellisButtonVariantProps } from 'pricing-pages-ui-components/utils/buttonVariantUtils';
import { useSelector } from 'react-redux';
import { CRM_SUITE_STARTER_BUNDLE, HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { FeatureTableBadge } from 'ui-addon-upgrades/featureTable/sharedComponents';
import { SCREENS } from '../../constants/tracking/Screens';
import { SSM0010_RECOMMENDED_STARTER_FEATURE_TABLE } from '../../constants/tracking/TrackingSources';
import { SuiteCTA } from '../../features/purchaseMotions/components/SuiteCTA';
import useSuitePurchaseMotions from '../../features/purchaseMotions/hooks/useSuitePurchaseMotions';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useFeaturesForApiNames } from '../../hooks/useFeatures';
import { useSuiteRecommendationModelFeedback } from '../../hooks/useRecommendations';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { productsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import withErrorBoundary from '../error-boundary/withErrorBoundary';
import FeatureTableFreePriceDisplay from '../feature-table/FeatureTableFreePriceDisplay';
import { buildBodyDataBySubCategory } from '../feature-table/suite-feature-table/buildTableUtils';
import CrmSuitePriceFeatureTable from '../merch-card/price-display/CrmSuitePriceFeatureTable';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const buildSuiteStarterHeaderData = (isAppReady, isPricingAndPurchasingDataReady, starterPurchaseMotionsJSX, isFreePortal) => {
  const ctaVariantStyles = getTrellisButtonVariantProps({
    variant: 'table-header',
    visible: isFreePortal
  });
  return {
    'hubspot-free': {
      Content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgradePages.products.crm.FREE"
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlock: "100",
          as: "div",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !isPricingAndPurchasingDataReady,
            heights: [19],
            widths: [190],
            children: /*#__PURE__*/_jsx(FeatureTableFreePriceDisplay, {})
          })
        }), /*#__PURE__*/_jsx(CurrentPlanButton, Object.assign({}, ctaVariantStyles))]
      })
    },
    'crm-suite-starter-bundle': {
      Content: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(FeatureTableBadge, {
            badgeType: "recommended"
          })
        }), /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgradePages.products.crm.STARTER"
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlock: "100",
          as: "div",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !isPricingAndPurchasingDataReady || !isAppReady,
            heights: [19],
            widths: [190],
            children: /*#__PURE__*/_jsx(CrmSuitePriceFeatureTable, {
              productLevel: STARTER
            })
          })
        }), starterPurchaseMotionsJSX]
      })
    }
  };
};
const RecommendedStarterFeatureTableContainer = () => {
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const ownedProducts = useSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return [];
    return productsFromProductInventorySelector(state);
  });
  const isFreePortal = hasPricingAndPurchasingSucceeded && ownedProducts && ownedProducts.length <= 0;
  const purchaseMotions = useSuitePurchaseMotions({
    // fall back to [] if not found
    productLevel: STARTER,
    returnFirstCTAOnly: true
  });
  const highlightedProducts = [HUBSPOT_FREE, CRM_SUITE_STARTER_BUNDLE];
  const {
    features,
    loading,
    error
  } = useFeaturesForApiNames(highlightedProducts);
  const {
    onClick
  } = useSuiteRecommendationModelFeedback({
    productLevel: STARTER
  });
  if (loading) {
    return null;
  }
  if (error) {
    throw new CustomError('Failed to fetch features', {
      error,
      highlightedProducts
    });
  }
  const starterPurchaseMotionsJSX = purchaseMotions.map(purchaseMotion => /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(SuiteCTA, Object.assign({
      purchaseMotion: purchaseMotion,
      productLevel: STARTER,
      trackingSource: SSM0010_RECOMMENDED_STARTER_FEATURE_TABLE,
      screen: SCREENS.RECOMMENDED,
      onClick: onClick,
      purchaseMotionLocation: 'GENERIC' // replace with FEATURE_TABLE when available https://git.hubteam.com/HubSpot/pricing-pages-unified-ui/issues/11974
    }, getButtonVariantProps({
      variant: 'table-header'
    })))
  }, purchaseMotion));
  const headerData = buildSuiteStarterHeaderData(isAppReady, hasPricingAndPurchasingSucceeded, starterPurchaseMotionsJSX, isFreePortal);
  const bodyData = buildBodyDataBySubCategory(features, SCREENS.RECOMMENDED);
  return /*#__PURE__*/_jsx(FeatureTable, {
    headerData: headerData,
    bodyData: bodyData
    // canViewNewSeats doesn't apply to free and suite products but we need to provide a value
    ,
    canViewNewSeats: false
  });
};
export default withErrorBoundary(RecommendedStarterFeatureTableContainer, {
  fallback: null,
  errorBoundaryName: 'RECOMMENDED_STARTER_FEATURE_TABLE_CONTAINER'
});